import React from "react";

import SettingsIcon from "@common/assets/images/members/change-role-icon.svg";
import LeaveIcon from "@common/assets/images/members/leave-team-icon.svg";
import RemoveIcon from "@common/assets/images/members/remove-member-icon.svg";
import RoundedCanvas from "@common/components/RoundedCanvas/RoundedCanvas";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { getUserInitials } from "@common/models";
import { Member, MemberRole } from "@common/models/Member.model";
import { vars } from "@common/styles";
import identifiers from "@common/util/identifiers.json";
import { getRandomColor } from "@main/pages/shared/helpers/getRandomColorHelper";
import * as Styled from "main/pages/Administration/components/TeamMembersCard/TeamMemberRows/TeamMemberRow.styles";

export const TeamMemberRow = ({
  member,
  currentUserIsAdmin,
  currentUserId,
  onSelectDeleteMember,
  onSelectCustomizeMember,
}: {
  member: Member;
  currentUserIsAdmin: boolean;
  currentUserId: string;
  onSelectDeleteMember: () => void;
  onSelectCustomizeMember: () => void;
}) => {
  const { user } = member;
  const { id: userId, email, name } = user;

  const isRowCurrentUser = currentUserId === userId;
  const tag: TagDetails = getTag(member.role);

  return (
    <Styled.Row>
      <div style={{ display: "flex", flex: 2, alignItems: "center" }}>
        <RoundedCanvas
          letter={getUserInitials(name)}
          bgColor={getRandomColor(userId) && getRandomColor(userId).background}
          txtColor={getRandomColor(userId) && getRandomColor(userId).text}
          size="small"
          image={member.user.picture ?? undefined}
        />
        <Styled.MemberDetails>
          <Styled.Name
            p1
            data-testid={identifiers["member.myTeam.user.name"]}
          >
            {name}
          </Styled.Name>
          <Styled.Email
            p2
            data-testid={identifiers["member.myTeam.user.email"]}
          >
            {email}
          </Styled.Email>
        </Styled.MemberDetails>
      </div>

      <div style={{ display: "flex", flex: 1, alignItems: "center" }}>
        <Styled.RowTag
          color={tag.color}
          value={tag.text}
          border
          borderColor={vars.colors.grey90}
        />
        {isRowCurrentUser ? (
          <Styled.RowTag
            color={vars.colors.purple}
            value={"you"}
            border
            borderColor={vars.colors.grey90}
          />
        ) : null}
      </div>

      <div style={{ display: "flex", flexBasis: "10rem", alignItems: "center", justifyContent: "end" }}>
        {currentUserIsAdmin || isRowCurrentUser ? (
          <Styled.ActionsWrap className={"members-row-actions"}>
            {currentUserIsAdmin ? (
              <Tooltip
                text={"Settings"}
                position={"top"}
              >
                <Styled.ActionButton onClick={onSelectCustomizeMember}>
                  <img
                    src={SettingsIcon}
                    alt={"Settings"}
                  />
                </Styled.ActionButton>
              </Tooltip>
            ) : null}
            {isRowCurrentUser ? (
              <Tooltip
                text={"Leave"}
                position={"top"}
              >
                <Styled.ActionButton onClick={onSelectDeleteMember}>
                  <img
                    src={LeaveIcon}
                    alt={"Leave"}
                  />
                </Styled.ActionButton>
              </Tooltip>
            ) : currentUserIsAdmin ? (
              <Tooltip
                text={"Remove"}
                position={"top"}
              >
                <Styled.ActionButton onClick={onSelectDeleteMember}>
                  <img
                    src={RemoveIcon}
                    alt={"Remove"}
                  />
                </Styled.ActionButton>
              </Tooltip>
            ) : null}
          </Styled.ActionsWrap>
        ) : null}
      </div>
    </Styled.Row>
  );
};

export interface TagDetails {
  text: string;
  color: string;
}

export const getTag = (role: MemberRole): TagDetails => {
  if (role === MemberRole.ADMIN) {
    return {
      text: "admin",
      color: vars.colors.green,
    };
  }
  if (role === MemberRole.MEMBER) {
    return {
      text: "member",
      color: vars.colors.orange,
    };
  }
  return {
    text: "guest",
    color: vars.colors.lavender,
  };
};
